import type { CrmData } from "../../types";
import { Drawer } from "../ui/Modal";
import { Badge, CompanyStatusBadge, InvoiceStatusBadge, StageBadge } from "../ui/Badge";
import { Avatar } from "../ui/Avatar";
import { companyContacts, companyOpportunities, companyRecentActivities, openOpportunitiesCount, totalPipelineValue, totalWonRevenue } from "../../lib/selectors";
import { computeInvoiceTotals, effectiveInvoiceStatus, invoicesForCompany, outstandingBalance, overdueAmount } from "../../lib/billing";
import { formatDate, money } from "../../lib/format";

export function CompanyDrawer({
  companyId,
  data,
  now,
  onClose,
  onOpenOpportunity,
  onOpenInvoice,
}: {
  companyId: string;
  data: CrmData;
  now: Date;
  onClose: () => void;
  onOpenOpportunity: (opportunityId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
}) {
  const company = data.companies.find((c) => c.id === companyId);
  if (!company) return null;

  const contacts = companyContacts(companyId, data.contacts);
  const opportunities = companyOpportunities(companyId, data.opportunities);
  const openOpps = opportunities.filter((o) => o.stage !== "Won" && o.stage !== "Lost");
  const wonOpps = opportunities.filter((o) => o.stage === "Won");
  const recentActivities = companyRecentActivities(companyId, data.opportunities, data.activities, 6);
  const invoices = invoicesForCompany(companyId, data.invoices);
  const companyOutstanding = outstandingBalance(invoices);
  const companyOverdue = overdueAmount(invoices, now);

  return (
    <div>
      <Drawer
        onClose={onClose}
        title={
          <div class="row" style={{ gap: "13px", alignItems: "flex-start" }}>
            <Avatar name={company.name} size="lg" />
            <div>
              <h2 class="drawer__opp-name">{company.name}</h2>
              <div class="drawer__badges">
                <CompanyStatusBadge status={company.status} />
                <span class="muted" style={{ textTransform: "capitalize" }}>
                  {company.industry}
                </span>
                <span class="muted">{company.size}</span>
                <span class="muted">{company.country}</span>
              </div>
            </div>
          </div>
        }
      >
        <div class="drawer-grid">
          <div class="drawer-grid__stats">
            <div class="stat">
              <span class="stat__label">Open opportunities</span>
              <span class="stat__value">{openOpportunitiesCount(companyId, data.opportunities)}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Open pipeline value</span>
              <span class="stat__value">{money(totalPipelineValue(companyId, data.opportunities))}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Total revenue (Won)</span>
              <span class="stat__value">{money(totalWonRevenue(companyId, data.opportunities))}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Account owner</span>
              <span class="stat__value">{company.accountOwner}</span>
            </div>
          </div>

          <section class="drawer-section">
            <h3>Contacts</h3>
            {contacts.length === 0 && <p class="empty-inline">No contacts on file.</p>}
            {contacts.map((c) => (
              <div key={c.id} class="contact-card">
                <Avatar name={c.name} size="md" />
                <div class="contact-card__body">
                  <div class="contact-card__name">
                    {c.name}
                    {c.decisionMaker && <Badge tone="success">Decision maker</Badge>}
                  </div>
                  <div class="contact-card__role">
                    {c.role} · {c.email} · {c.phone}
                  </div>
                </div>
              </div>
            ))}
          </section>

          <section class="drawer-section">
            <h3>Open Opportunities</h3>
            {openOpps.length === 0 && <p class="empty-inline">No open opportunities.</p>}
            {openOpps.map((o) => (
              <button
                type="button"
                key={o.id}
                class="opp-row"
                data-ai-id={`open-opportunity-${o.id}`}
                onClick={() => onOpenOpportunity(o.id)}
              >
                <span class="opp-row__name">{o.name}</span>
                <StageBadge stage={o.stage} />
                <span class="opp-row__value">{money(o.value)}</span>
              </button>
            ))}
          </section>

          <section class="drawer-section">
            <h3>Won Opportunities</h3>
            {wonOpps.length === 0 && <p class="empty-inline">No won opportunities yet.</p>}
            {wonOpps.map((o) => (
              <button
                type="button"
                key={o.id}
                class="opp-row"
                data-ai-id={`open-opportunity-${o.id}`}
                onClick={() => onOpenOpportunity(o.id)}
              >
                <span class="opp-row__name">{o.name}</span>
                <StageBadge stage={o.stage} />
                <span class="opp-row__value">{money(o.finalValue ?? o.value)}</span>
              </button>
            ))}
          </section>

          <section class="drawer-section">
            <div class="row spread">
              <h3>Billing</h3>
              {companyOverdue > 0 && <Badge tone="danger">{money(companyOverdue)} overdue</Badge>}
            </div>
            <div class="billing-mini">
              <div>
                <span class="stat__label">Outstanding</span>
                <span class="stat__value">{money(companyOutstanding)}</span>
              </div>
              <div>
                <span class="stat__label">Overdue</span>
                <span class="stat__value">{money(companyOverdue)}</span>
              </div>
            </div>
            {invoices.length === 0 && <p class="empty-inline">No invoices for this account.</p>}
            {invoices.map((inv) => (
              <button
                type="button"
                key={inv.id}
                class="opp-row"
                data-ai-id={`open-invoice-${inv.id}`}
                onClick={() => onOpenInvoice(inv.id)}
              >
                <span class="opp-row__name num">{inv.invoiceNumber}</span>
                <InvoiceStatusBadge status={effectiveInvoiceStatus(inv, now)} />
                <span class="opp-row__value">{money(computeInvoiceTotals(inv).total)}</span>
              </button>
            ))}
          </section>

          <section class="drawer-section">
            <h3>Recent Activities</h3>
            {recentActivities.length === 0 && <p class="empty-inline">No recent activity.</p>}
            <ol class="timeline">
              {recentActivities.map((a) => (
                <li key={a.id} class="timeline__item">
                  <span class="timeline__icon" aria-hidden="true">
                    •
                  </span>
                  <div class="timeline__content">
                    <div class="timeline__meta">
                      <strong>{a.author}</strong> · {formatDate(a.timestamp)}
                    </div>
                    <div class="timeline__text">{a.content}</div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {company.notes && (
            <section class="drawer-section">
              <h3>Notes</h3>
              <p>{company.notes}</p>
            </section>
          )}
        </div>
      </Drawer>
    </div>
  );
}
